import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Timeline, Collapse, Tag, Typography, Space, Button, Modal,
  Select, Input, message, Tooltip,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  RetweetOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { WorkflowStatusResponse, WorkflowTransition, RfoPhase } from '@/types/workflow';
import type { ReviewGateDto } from '@/types/review';
import type { User } from '@/types/common';
import type { ComplianceMatrixData } from '@/types/compliance';
import { getPhaseColor, getPhaseDescription } from '@/utils/phaseHelpers';
import { workflowApi } from '@/api/workflowApi';
import { userApi } from '@/api/userApi';
import { bidAnalysisApi, type BidAnalysisDto } from '@/api/bidAnalysisApi';
import { kickoffAnalysisApi, type KickoffAnalysisDto } from '@/api/kickoffAnalysisApi';
import { analysisApi, type ProposalAnalysisDto } from '@/api/analysisApi';
import { complianceApi } from '@/api/complianceApi';
import { qualificationApi, type BidDecisionData } from '@/api/qualificationApi';
import { useAuth } from '@/auth/useAuth';
import dayjs from 'dayjs';

import type { UserRole } from '@/types/common';

const { Text } = Typography;
const { TextArea } = Input;

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO', BD_MANAGER: 'BD Manager', ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect', OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager', TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead', CONTRIBUTOR: 'Contributor', ADMIN: 'Admin',
};

interface PipelineSummaryProps {
  workflowStatus: WorkflowStatusResponse;
  reviewGates: ReviewGateDto[];
  opportunityId: string;
  onRefresh: () => void;
}

const GATE_TYPE_LABELS: Record<string, string> = {
  TECHNICAL: 'E.1 Technical Review',
  STRATEGY_DELIVERY: 'E.2 Strategy & Delivery Review',
  COMPLIANCE: 'E.3 Compliance Review',
  EXECUTIVE: 'E.4 Executive Review & Signature',
};

const reviewStatusColors: Record<string, string> = {
  PENDING: 'default',
  IN_PROGRESS: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  REWORK: 'warning',
};

const formatRole = (role: string) =>
  role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PipelineSummary: React.FC<PipelineSummaryProps> = ({
  workflowStatus,
  reviewGates,
  opportunityId,
  onRefresh,
}) => {
  const { hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const canManagePhase = hasAnyRole(['BD_MANAGER', 'ADMIN', 'CEO']);

  // ── Move-to-phase state ──────────────────────────────────────
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [moveTargetPhase, setMoveTargetPhase] = useState<RfoPhase | undefined>(undefined);
  const [moveReason, setMoveReason] = useState('');
  const [moveAssignUserId, setMoveAssignUserId] = useState<string | undefined>(undefined);
  const [movingPhase, setMovingPhase] = useState(false);

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // ── AI analysis & gate detail state ──────────────────────────
  const [bidAnalysis, setBidAnalysis] = useState<BidAnalysisDto | null>(null);
  const [bidDecision, setBidDecision] = useState<BidDecisionData | null>(null);
  const [kickoffAnalysis, setKickoffAnalysis] = useState<KickoffAnalysisDto | null>(null);
  const [proposalAnalysis, setProposalAnalysis] = useState<ProposalAnalysisDto | null>(null);
  const [complianceMatrix, setComplianceMatrix] = useState<ComplianceMatrixData | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await userApi.getAll({ size: 100, isActive: true });
      setUsers(response.content || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (moveModalVisible) {
      fetchUsers();
    }
  }, [moveModalVisible, fetchUsers]);

  // ── Fetch AI analysis data for gates B, C, D ─────────────────
  useEffect(() => {
    const fetchAnalysisData = async () => {
      const results = await Promise.allSettled([
        bidAnalysisApi.getLatest(opportunityId).then((r) => r.data),
        qualificationApi.getBidDecision(opportunityId),
        kickoffAnalysisApi.getLatest(opportunityId).then((r) => r.data),
        analysisApi.getLatest(opportunityId).then((r) => r.data),
        complianceApi.getMatrix(opportunityId),
      ]);
      if (results[0].status === 'fulfilled') setBidAnalysis(results[0].value);
      if (results[1].status === 'fulfilled' && results[1].value) setBidDecision(results[1].value);
      if (results[2].status === 'fulfilled') setKickoffAnalysis(results[2].value);
      if (results[3].status === 'fulfilled') setProposalAnalysis(results[3].value);
      if (results[4].status === 'fulfilled' && results[4].value) setComplianceMatrix(results[4].value);
    };
    fetchAnalysisData();
  }, [opportunityId]);

  // ── Find the E.4 (EXECUTIVE) gate for pipeline display ──────
  const parallelGates = reviewGates.filter((g) => g.gateType !== 'EXECUTIVE');

  const handleMoveToPhase = async () => {
    if (!moveTargetPhase) {
      message.warning('Please select a target phase');
      return;
    }
    if (!moveReason.trim()) {
      message.warning('Please provide a reason');
      return;
    }
    setMovingPhase(true);
    try {
      await workflowApi.moveToPhase(
        opportunityId,
        moveTargetPhase,
        moveReason.trim(),
        moveAssignUserId,
      );
      message.success(`Workflow moved to ${moveTargetPhase.replace('PHASE_', 'Phase ')}`);
      setMoveModalVisible(false);
      setMoveTargetPhase(undefined);
      setMoveReason('');
      setMoveAssignUserId(undefined);
      onRefresh();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to move workflow');
    } finally {
      setMovingPhase(false);
    }
  };

  // ── Build transition map: fromPhase → who completed that phase ──
  const phaseCompletedByMap = useMemo(() => {
    const map: Record<string, WorkflowTransition> = {};
    for (const t of workflowStatus.transitions) {
      if (t.fromPhase && !t.reason?.startsWith('[ROLLBACK]')) {
        map[t.fromPhase] = t; // last non-rollback transition FROM this phase = who completed it
      }
    }
    return map;
  }, [workflowStatus.transitions]);

  // ── Render AI/gate details per phase ───────────────────────────
  const renderPhaseDetails = (phase: RfoPhase) => {
    const detailStyle = { marginTop: 6, padding: '6px 10px', background: 'var(--color-bg-muted)', borderLeft: '3px solid var(--color-border)', borderRadius: 4 };

    if (phase === 'PHASE_B' && (bidDecision || (bidAnalysis?.status === 'COMPLETED'))) {
      return (
        <div style={detailStyle}>
          {bidDecision && (
            <div>
              <Tag
                color={bidDecision.decision === 'GO' ? 'success' : bidDecision.decision === 'NO_GO' ? 'error' : 'warning'}
                style={{ fontSize: 10 }}
              >
                {bidDecision.decision.replace(/_/g, ' ')}
              </Tag>
              {bidDecision.decidedBy && (
                <Text type="secondary" style={{ fontSize: 11 }}>by {bidDecision.decidedBy}</Text>
              )}
              {bidDecision.rationale && (
                <Text style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                  {bidDecision.rationale.length > 120 ? bidDecision.rationale.slice(0, 120) + '...' : bidDecision.rationale}
                </Text>
              )}
            </div>
          )}
          {bidAnalysis?.status === 'COMPLETED' && (
            <div style={{ marginTop: bidDecision ? 6 : 0 }}>
              <ExperimentOutlined style={{ color: 'var(--color-purple)', marginRight: 4 }} />
              <Text style={{ fontSize: 11, color: 'var(--color-purple)' }}>
                AI: {bidAnalysis.recommendation} ({bidAnalysis.confidence}% confidence)
              </Text>
              {bidAnalysis.summary && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                  {bidAnalysis.summary.length > 150 ? bidAnalysis.summary.slice(0, 150) + '...' : bidAnalysis.summary}
                </Text>
              )}
            </div>
          )}
        </div>
      );
    }

    if (phase === 'PHASE_C' && kickoffAnalysis?.status === 'COMPLETED') {
      return (
        <div style={detailStyle}>
          <ExperimentOutlined style={{ color: 'var(--color-purple)', marginRight: 4 }} />
          <Text style={{ fontSize: 11, color: 'var(--color-purple)' }}>
            AI Readiness: {kickoffAnalysis.readinessScore != null ? `${kickoffAnalysis.readinessScore}/100` : 'N/A'}
          </Text>
          {kickoffAnalysis.strategySummary && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
              {kickoffAnalysis.strategySummary.length > 150 ? kickoffAnalysis.strategySummary.slice(0, 150) + '...' : kickoffAnalysis.strategySummary}
            </Text>
          )}
        </div>
      );
    }

    if (phase === 'PHASE_D' && (complianceMatrix || proposalAnalysis?.status === 'COMPLETED')) {
      return (
        <div style={detailStyle}>
          {complianceMatrix && (
            <div>
              <FileTextOutlined style={{ marginRight: 4 }} />
              <Text style={{ fontSize: 11 }}>
                Compliance: {complianceMatrix.compliantCount}/{complianceMatrix.totalRequirements} compliant
                {complianceMatrix.partialCount > 0 && `, ${complianceMatrix.partialCount} partial`}
                {complianceMatrix.nonCompliantCount > 0 && `, ${complianceMatrix.nonCompliantCount} non-compliant`}
              </Text>
            </div>
          )}
          {proposalAnalysis?.status === 'COMPLETED' && (
            <div style={{ marginTop: complianceMatrix ? 6 : 0 }}>
              <ExperimentOutlined style={{ color: 'var(--color-purple)', marginRight: 4 }} />
              <Text style={{ fontSize: 11, color: 'var(--color-purple)' }}>
                AI Proposal Score: {proposalAnalysis.overallScore}/100
                {proposalAnalysis.passed !== undefined && (
                  <Tag
                    color={proposalAnalysis.passed ? 'success' : 'error'}
                    style={{ fontSize: 9, marginLeft: 6 }}
                  >
                    {proposalAnalysis.passed ? 'Passed' : 'Below Threshold'}
                  </Tag>
                )}
              </Text>
              {proposalAnalysis.summary && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                  {proposalAnalysis.summary.length > 150 ? proposalAnalysis.summary.slice(0, 150) + '...' : proposalAnalysis.summary}
                </Text>
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // ── Build timeline items ─────────────────────────────────────
  const activePhases = workflowStatus.phases.filter((p) => p.status !== 'upcoming');

  const timelineItems = activePhases.map((phaseInfo) => {
    const isCompleted = phaseInfo.status === 'completed';
    const isCurrent = phaseInfo.status === 'current';
    const color = isCompleted ? '#52c41a' : getPhaseColor(phaseInfo.phase as RfoPhase);
    const transition = phaseCompletedByMap[phaseInfo.phase];
    const completedBy = transition?.transitionedBy;
    const reason = transition?.reason;

    return {
      color,
      dot: isCompleted ? (
        <CheckCircleOutlined style={{ fontSize: 16 }} />
      ) : isCurrent ? (
        <LoadingOutlined style={{ fontSize: 16 }} />
      ) : (
        <ClockCircleOutlined style={{ fontSize: 16 }} />
      ),
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
            <Space size={6} wrap>
              <Text strong style={{ fontSize: 13 }}>{phaseInfo.gateLabel}</Text>
              <Tag
                color={isCompleted ? 'success' : 'processing'}
                style={{ fontSize: 10 }}
              >
                {isCompleted ? 'Completed' : 'Current'}
              </Tag>
              {phaseInfo.documentCount > 0 && (
                <Tooltip title="View in Content Library">
                  <Tag
                    icon={<FileTextOutlined />}
                    color="blue"
                    style={{ fontSize: 10, cursor: 'pointer' }}
                    onClick={() => navigate('/content-library')}
                  >
                    {phaseInfo.documentCount} artifact{phaseInfo.documentCount !== 1 ? 's' : ''}
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
              {phaseInfo.enteredAt && dayjs(phaseInfo.enteredAt).format('MMM D, YYYY')}
              {phaseInfo.completedAt && (
                <span> — {dayjs(phaseInfo.completedAt).format('MMM D, YYYY')}</span>
              )}
            </div>
          </div>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {completedBy
                ? `Completed by ${completedBy}`
                : 'In progress'}
              <span style={{ margin: '0 6px', color: 'var(--color-text-disabled)' }}>|</span>
              {getPhaseDescription(phaseInfo.phase as RfoPhase)}
            </Text>
          </div>
          {reason && (
            <div style={{ marginTop: 2 }}>
              <Text italic style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                Note: {reason}
              </Text>
            </div>
          )}
          {renderPhaseDetails(phaseInfo.phase as RfoPhase)}
        </div>
      ),
    };
  });

  // ── Build review gate summary (E.1-E.3 only) ──────────────
  const sortedParallelGates = [...parallelGates].sort((a, b) => a.gateOrder - b.gateOrder);
  const approvedCount = sortedParallelGates.filter((g) => g.status === 'APPROVED').length;

  // ── Phase options for move modal ─────────────────────────────
  const movePhaseOptions = workflowStatus.phases
    .filter((p) => p.status === 'completed')
    .map((p) => ({
      value: p.phase,
      label: p.gateLabel,
    }));

  return (
    <>
      {/* ── Phase Management (role-gated) — shown above pipeline.
           Hidden when no phases are completed (e.g. workflow still in Gate A)
           because there is nothing earlier to move back to. ── */}
      {canManagePhase && movePhaseOptions.length > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <RetweetOutlined style={{ color: '#d48806' }} />
              <Text strong style={{ fontSize: 13 }}>Phase Management</Text>
            </Space>
          }
          style={{ marginBottom: 16, borderColor: 'var(--color-warning)' }}
        >
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Move this opportunity back to any earlier phase for rework. You can optionally
            reassign the phase owner. The pipeline will restart from the selected phase.
          </Text>
          <Button
            icon={<RetweetOutlined />}
            onClick={() => setMoveModalVisible(true)}
          >
            Move to Earlier Phase
          </Button>
        </Card>
      )}

      {/* ── Section 1: Pipeline Journey ─────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <ClockCircleOutlined style={{ color: 'var(--color-warning)' }} />
            <Text strong style={{ fontSize: 14 }}>Pipeline Journey</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Who did what at each gate
            </Text>
          </Space>
        }
        style={{ marginTop: 12, marginBottom: 16 }}
      >
        <Timeline items={timelineItems} />
      </Card>

      {/* ── Section 2: Phase E Review Results (E.1-E.3) ─────────── */}
      {sortedParallelGates.length > 0 && (
        <Collapse
          size="small"
          style={{ marginBottom: 16 }}
          defaultActiveKey={['review-summary']}
          items={[
            {
              key: 'review-summary',
              label: (
                <Space>
                  <SafetyCertificateOutlined style={{ color: 'var(--color-primary)' }} />
                  <Text strong style={{ fontSize: 13 }}>Phase E Review Results</Text>
                  <Tag
                    color={approvedCount === sortedParallelGates.length ? 'success' : 'warning'}
                    style={{ fontSize: 10 }}
                  >
                    {approvedCount}/{sortedParallelGates.length} Approved
                  </Tag>
                </Space>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {sortedParallelGates.map((gate) => {
                    const isApproved = gate.status === 'APPROVED';
                    const isRejected = gate.status === 'REJECTED';
                    return (
                      <div
                        key={gate.id}
                        style={{
                          padding: '8px 12px',
                          background: isApproved ? 'var(--color-success-light)' : isRejected ? 'var(--color-error-light)' : 'var(--color-warning-light)',
                          borderLeft: `3px solid ${isApproved ? 'var(--color-success)' : isRejected ? 'var(--color-error)' : 'var(--color-warning)'}`,
                          borderRadius: 4,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                          <Space size={6}>
                            <Tag
                              color={reviewStatusColors[gate.status] || 'default'}
                              style={{ fontSize: 11 }}
                            >
                              {gate.status}
                            </Tag>
                            <Text strong style={{ fontSize: 12 }}>
                              {GATE_TYPE_LABELS[gate.gateType] || gate.gateType}
                            </Text>
                            {gate.reviewer && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                by {gate.reviewer}
                              </Text>
                            )}
                          </Space>
                          {gate.completedAt && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {dayjs(gate.completedAt).format('MMM D, YYYY h:mm A')}
                            </Text>
                          )}
                        </div>
                        {gate.feedback && (
                          <div style={{ marginTop: 4 }}>
                            <Text italic style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                              &ldquo;{gate.feedback}&rdquo;
                            </Text>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Space>
              ),
            },
          ]}
        />
      )}

      {/* ── Move-to-phase modal ──────────────────────────────────── */}
      <Modal
        title="Move to Earlier Phase"
        open={moveModalVisible}
        onCancel={() => {
          setMoveModalVisible(false);
          setMoveTargetPhase(undefined);
          setMoveReason('');
          setMoveAssignUserId(undefined);
        }}
        onOk={handleMoveToPhase}
        okText="Move & Restart Pipeline"
        okButtonProps={{ danger: true, loading: movingPhase }}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              Target Phase <span style={{ color: 'var(--color-error)' }}>*</span>
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select phase to move to"
              value={moveTargetPhase}
              onChange={(v) => setMoveTargetPhase(v)}
              options={movePhaseOptions}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              Assign To (optional)
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select user to assign as phase owner"
              value={moveAssignUserId}
              onChange={(v) => setMoveAssignUserId(v)}
              allowClear
              showSearch
              optionFilterProp="label"
              loading={loadingUsers}
              options={users.map((u) => ({
                value: u.id,
                label: `${u.fullName} (${formatRole(u.role)})`,
              }))}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              Reason <span style={{ color: 'var(--color-error)' }}>*</span>
            </Text>
            <TextArea
              value={moveReason}
              onChange={(e) => setMoveReason(e.target.value)}
              placeholder="Why is this being moved back? What needs to be addressed?"
              rows={3}
            />
          </div>
          <Tooltip title="This will move the workflow back to the selected phase. All review gates will be cleared if moving from Phase E or later.">
            <Text type="warning" style={{ fontSize: 11 }}>
              Note: Moving back will clear Phase E review gates and restart from the selected phase.
            </Text>
          </Tooltip>
        </Space>
      </Modal>
    </>
  );
};

export default PipelineSummary;
